import React from 'react';

import type CadenceState from '@poseidon/types/pg-gen/cadenceState';
import { cn } from '@poseidon/components/cn';
import { Badge } from '@poseidon/components/Badge';
import { Button } from '@poseidon/components/Input2';
import { Arrows, Time } from '@poseidon/components/StrokeIcons';
import { WaveSpinner } from '@poseidon/components/WaveSpinner';

import { api } from '@/utils/api';
import { SmallPaddedScrollable } from '@poseidon/components/Scrollable';

type CampaignListViewProps = {
  contactId: string;
  onSelectCampaign: (campaignId: string) => void;
  onBack?: () => void;
};

function getStateLabel(state: CadenceState): string {
  const labels: Record<CadenceState, string> = {
    'active': 'Active',
    'paused': 'Paused',
    'finished': 'Finished',
    'interrupted': 'Stopped',
    'responded': 'Replied',
    'deleted': 'Deleted',
    'not-started': 'Not Started',
    'pending-review': 'Pending Review',
    'rejected': 'Rejected',
    'contact_unavailable': 'Unavailable',
    'action-required': 'Action Required',
    'researching': 'Researching',
  };
  return labels[state] ?? state;
}

function getStateBadgeType(state: CadenceState): 'success' | 'warning' | 'dangerous' | 'standard' {
  const types: Record<CadenceState, 'success' | 'warning' | 'dangerous' | 'standard'> = {
    'active': 'success',
    'paused': 'warning',
    'finished': 'standard',
    'interrupted': 'dangerous',
    'responded': 'success',
    'deleted': 'dangerous',
    'not-started': 'standard',
    'pending-review': 'warning',
    'rejected': 'dangerous',
    'contact_unavailable': 'dangerous',
    'action-required': 'warning',
    'researching': 'warning',
  };
  return types[state] ?? 'standard';
}

function formatDate(date: Date | null): string {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function CampaignListView({ contactId, onSelectCampaign, onBack }: CampaignListViewProps) {
  const campaignsQuery = api.cadence.forContact.useQuery(
    { contactId },
    { enabled: Boolean(contactId) },
  );

  if (campaignsQuery.isPending) {
    return (
      <SmallPaddedScrollable className="flex items-center justify-center">
        <WaveSpinner />
      </SmallPaddedScrollable>
    );
  }

  const campaigns = campaignsQuery.data ?? [];

  if (campaigns.length === 0) {
    return (
      <SmallPaddedScrollable className="flex flex-col items-center justify-center gap-2 text-slate-500 dark:text-slate-400">
        <Time.ClockRewind className="size-8 opacity-50" />
        <p className="text-sm">No campaigns for this contact</p>
        {onBack && (
          <Button size="sm" onClick={onBack} icon={<Arrows.ArrowLeft />}>
            Back to Contact Info
          </Button>
        )}
      </SmallPaddedScrollable>
    );
  }

  return (
    <SmallPaddedScrollable innerClassName="p-0">
      {/* Header with back button */}
      {onBack && (
        <div className="sticky top-0 z-10 border-b border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900">
          <div className="flex items-center gap-2">
            <Button
              size="xs"
              color="blue" tone="outline"
              onClick={onBack}
              icon={<Arrows.ArrowLeft />}
            />
            <span className="font-medium text-slate-900 dark:text-slate-100">
              All Campaigns
            </span>
          </div>
        </div>
      )}
      <div className="flex flex-col">
        {campaigns.map((campaign) => (
          <div
            key={campaign.id}
            onClick={() => onSelectCampaign(campaign.id)}
            className={cn(
              'flex cursor-pointer flex-col gap-2 border-b border-slate-200 px-4 py-3 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800',
              campaign.isActive && 'bg-green-50/50 dark:bg-green-900/20',
              (campaign.state === 'deleted' || campaign.state === 'contact_unavailable') && 'opacity-60',
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <span
                className={cn(
                  'text-sm font-medium text-slate-900 dark:text-slate-100',
                  campaign.state === 'deleted' && 'line-through',
                )}
              >
                {campaign.name}
              </span>
              <Badge size="xs" type={getStateBadgeType(campaign.state)} dot>
                {getStateLabel(campaign.state)}
              </Badge>
            </div>

            <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
              <span>
                Created: {formatDate(campaign.createdOn)}
              </span>
            </div>

            <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
              <span>
                Steps: {campaign.stepsRun ?? 0} / {campaign.stepsTotalCount ?? 0}
              </span>
              {campaign.templateName && (
                <span className="truncate">
                  Template: {campaign.templateName}
                </span>
              )}
            </div>

            {(campaign.replyOn || campaign.connectionAcceptedOn) && (
              <div className="flex items-center gap-2 text-xs">
                {campaign.replyOn && (
                  <Badge size="xs" type="success">
                    Replied {formatDate(campaign.replyOn)}
                  </Badge>
                )}
                {campaign.connectionAcceptedOn && (
                  <Badge size="xs" type="success">
                    Connected {formatDate(campaign.connectionAcceptedOn)}
                  </Badge>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </SmallPaddedScrollable>
  );
}
